import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button/Button';
import { Checkbox } from '../../ui/Checkbox/Checkbox';
import { ToneSelector } from './ToneSelector/ToneSelector';
import { AttachmentList } from './AttachmentList/AttachmentList';
import { Dialogue } from '../../components/Dialogue/Dialogue';
import { FileViewerModal } from './FileViewerModal/FileViewerModal';
import { ValidationMessage } from '../../components/Forms/ValidationMessage';
import { FormInput } from '../../ui/FormInput/FormInput';
import { TextArea } from '../../ui/TextArea/TextArea';
import type { FilePreview } from './FileViewerModal/types';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import { EmailBodySkeleton } from './EmailBodySkeleton';
import { EmailFormSkeleton } from './EmailFormSkeleton';
import { validateEmail } from '../../utils/validators';
import { TranslationKey } from '../../i18n/translationKeys';
import { localizeApiError } from '../../utils/apiErrors';
import { emailsService } from '../../services/emailsService';
import { bccEmailService } from '../../services/bccEmailService';
import { useNoAiStore } from '../../stores/noAiStore';
import { useToastStore } from '../../stores/toastStore';
import { relancesService } from '../../services/relancesService';
import { useEmailSendFlow } from './useEmailSendFlow';
import type { SendingAction } from './useEmailSendFlow';
import type { AutoAttachmentDto } from '../../types/customerFollowUp/autoAttachment';
import { sortAutoAttachments } from '../../utils/relance/sortAutoAttachments';
import { EmailTone } from '../../types/customerFollowUp/enums';
import { EMAIL_SUBJECT_MAX_LENGTH } from '../../types/customerFollowUp/email';
import type { AttachmentDto, RelanceEmailDto } from '../../types/customerFollowUp/email';
import { CUSTOM_TONE } from '../../types/customEmailTemplate';
import type { ToneChoice } from '../../types/customEmailTemplate';
import { useCustomTemplateOrchestration } from './useCustomTemplateOrchestration';
import { useNoAiBodySync } from './useNoAiBodySync';
import { CustomTemplateToolbar } from './CustomTemplate/CustomTemplateToolbar';
import { isCustomEmailTemplateEnabled } from '../../env';

const MAX_MANUAL_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const ALLOWED_MANUAL_ATTACHMENT_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.xlsx', '.docx'];
const MANUAL_ATTACHMENT_ACCEPT = ALLOWED_MANUAL_ATTACHMENT_EXTENSIONS.join(',');
const FIELD_ROW_CHROME = 'field-row-focus grid items-center gap-xs px-xl py-m';
const FIELD_ROW_CLASS = `${FIELD_ROW_CHROME} grid-cols-[3.5rem_1fr]`;

const bodyCacheKey = (tone: EmailTone, noAi: boolean) => `${tone}:${noAi}`;

interface EmailTabProps {
    relanceId: string;
    onSend?: () => void;
    onSendAndNext?: () => void;
    hasMissingMandatoryInformation?: boolean;
    /**
     * A sync is running: the amounts this draft describes are being recomputed, so sending now would send
     * figures about to change.
     *
     * The page also seals this subtree with `inert`, which blocks clicks *and* Tab-then-Enter. This is the
     * belt to that braces: `inert` is silently ignored by older engines, and the app runs inside the Acomba
     * desktop shell, so the destructive action carries a real `disabled` that no engine can overlook.
     */
    isSyncing?: boolean;
    /** Reports an in-flight send so the page can seal the whole card, the same way a sync does. */
    onSendingChange?: (sending: boolean) => void;
    /**
     * An AI run is in flight for this relance, independent of `isSyncing`: it can still be running after
     * the sync's veil lifts at `RelanceFill`. While true the body shows {@link EmailBodySkeleton}, sending
     * is disabled and the empty-body auto-regeneration is suppressed, since the saga's draft stage owns
     * the row. The draft reloads once this flips back to false.
     */
    inFlight?: boolean;
    /**
     * A backend run is writing this relance's draft. Only its end reloads the draft: unlocking without one
     * must not overwrite what the tab regenerated or the agent attached meanwhile. Defaults to `inFlight`.
     */
    runInFlight?: boolean;
    /**
     * How many ACCEO Transphere payment links the reminder can carry. Only the count reaches the
     * browser — the server composes the block from the URLs it resolved. Zero (or an unloaded null)
     * means no payment-link UI at all: not a disabled control, not a hint.
     */
    paymentLinkCount?: number | null;
    /** Where the send bar renders. The page passes a slot spanning its full height, so the bar stays in view from the top of the page; without one it renders in place. */
    sendBarContainer?: HTMLElement | null;
}

export const EmailTab: React.FC<EmailTabProps> = ({ relanceId, onSend, onSendAndNext, hasMissingMandatoryInformation = false, isSyncing = false, onSendingChange, inFlight = false, runInFlight = inFlight, paymentLinkCount = null, sendBarContainer = null }) => {
    const { t } = useTranslation();
    const noAi = useNoAiStore(s => s.noAi);
    const showToast = useToastStore(s => s.showToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [subject, setSubject] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [bccEmail, setBccEmail] = useState('');
    const [copiesOpen, setCopiesOpen] = useState(false);
    const initialToneRef = useRef(noAi ? EmailTone.COURTEOUS : EmailTone.PROFESSIONAL);
    const [selectedTone, setSelectedTone] = useState<EmailTone>(initialToneRef.current);
    const [body, setBody] = useState('');
    const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const aiDraftRef = useRef<RelanceEmailDto | null>(null);
    const [draftError, setDraftError] = useState<unknown>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [sent, setSent] = useState(false);
    const [previewFile, setPreviewFile] = useState<FilePreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sendingAction, setSendingAction] = useState<SendingAction | null>(null);
    const customDraft = useCustomTemplateOrchestration({
        relanceId,
        noAi,
        applyDraft: draft => {
            setSubject(draft.subject);
            setBody(draft.bodyTone);
        },
        stopStreaming: () => {
            abortActiveStream();
            setIsStreaming(false);
        },
    });
    const { loadInitial: loadInitialCustomAware, selectCustom } = customDraft;
    type EmailField = 'subject' | 'recipientEmail' | 'ccEmail' | 'bccEmail' | 'body';

    const [touched, setTouched] = useState<Partial<Record<EmailField, boolean>>>({});

    const errors = useMemo(() => {
        const errs: Partial<Record<EmailField, string>> = {};
        if (!subject.trim())
            errs.subject = t(TranslationKey.CustomerFollowUp.Email.Errors.SubjectRequired);
        else if (subject.length > EMAIL_SUBJECT_MAX_LENGTH)
            errs.subject = t(TranslationKey.CustomerFollowUp.Email.Errors.SubjectMaxLength);
        if (!recipientEmail.trim())
            errs.recipientEmail = t(TranslationKey.CustomerFollowUp.Email.Errors.EmailAddressRequired);
        else if (!validateEmail(recipientEmail.trim()))
            errs.recipientEmail = t(TranslationKey.CustomerFollowUp.Email.Errors.EmailAddressInvalid);
        if (ccEmail.trim() && !validateEmail(ccEmail.trim()))
            errs.ccEmail = t(TranslationKey.CustomerFollowUp.Email.Errors.EmailAddressInvalid);
        if (bccEmail.trim() && !validateEmail(bccEmail.trim()))
            errs.bccEmail = t(TranslationKey.CustomerFollowUp.Email.Errors.EmailAddressInvalid);
        if (!body.trim())
            errs.body = t(TranslationKey.CustomerFollowUp.Email.Errors.ContentRequired);
        return errs;
    }, [subject, recipientEmail, ccEmail, bccEmail, body, t]);

    const isValid = Object.keys(errors).length === 0;
    const getError = (field: EmailField) => touched[field] ? errors[field] : undefined;
    const touch = (field: EmailField) => () => setTouched(prev => ({ ...prev, [field]: true }));
    // An invalid copy address blocks sending, so its error must never sit behind a collapsed block.
    const copiesShown = copiesOpen || !!(getError('ccEmail') ?? getError('bccEmail'));

    // Set when the persisted draft loaded with an empty body (see loadInitialDraft)
    const needsInitialBodyRegenRef = useRef(false);
    // Cache: `${tone}:${noAi}` → streamed body (avoids redundant backend calls when switching back)
    const cachedBodiesRef = useRef<Map<string, string>>(new Map());
    // AbortController for any in-progress stream so we can cancel it on tone or AI-mode change
    const streamAbortRef = useRef<AbortController | null>(null);

    const [automaticAttachments, setAutomaticAttachments] = useState<AutoAttachmentDto[]>([]);
    // Default on: the reminder is meant to carry the link, and Josée opts out rather than opting in.
    const [includePaymentLinks, setIncludePaymentLinks] = useState(true);
    const hasPaymentLinks = (paymentLinkCount ?? 0) > 0;

    const {
        handleSend,
        pendingDeleteAutoAttachmentName,
        setPendingDeleteAutoAttachmentName,
        handleDeleteAutoAttachmentConfirm,
        attachmentFetchFailedFiles,
        setAttachmentFetchFailedFiles,
        handleAttachmentFetchFailedConfirm,
        showNoAttachmentsWarning,
        setShowNoAttachmentsWarning,
        handleNoAttachmentsConfirm,
    } = useEmailSendFlow({
        relanceId,
        getDraft: () => ({ ...customDraft.toSendable(subject, body), recipientEmail, selectedTone, attachments, ccEmail: ccEmail.trim() || null, bccEmail: bccEmail.trim() || null }),
        getAiDraft: () => aiDraftRef.current,
        getManualFiles: () => Array.from(manualFilesRef.current.values()),
        isValid,
        automaticAttachments,
        setAutomaticAttachments,
        // A relance with no links is unaffected either way, so the flag needs no guard here.
        includePaymentLinks,
        setLoading,
        onSendingChange,
        setSent,
        setSendingAction,
    });
    const sortedAutomaticAttachments = useMemo(
        () => sortAutoAttachments(automaticAttachments),
        [automaticAttachments]
    );
    const previewableAutoAttachments = useMemo(
        () => new Set(automaticAttachments.map(a => a.fileName)),
        [automaticAttachments]
    );

    // Maps fileName → { url, fileType, isBlob }
    // isBlob = true for local files (blob URLs must be revoked on cleanup)
    // isBlob = false for remote server URLs (no revocation needed)
    const blobUrlsRef = useRef<Map<string, { url: string; fileType: string | null; isBlob: boolean }>>(new Map());

    // Stores the actual File objects for manual attachments so they can be sent with the email
    const manualFilesRef = useRef<Map<string, File>>(new Map());

    // Tracks the file whose preview is currently being fetched so a result that arrives
    // after the user closed (or switched) the modal is discarded instead of reopening it.
    const previewRequestRef = useRef<string | null>(null);

    useEffect(() => {
        const urls = blobUrlsRef.current;
        return () => {
            for (const entry of urls.values()) {
                if (entry.isBlob) URL.revokeObjectURL(entry.url);
            }
            streamAbortRef.current?.abort();
        };
    }, []);

    const loadDraftTemplate = useCallback(async (tone: EmailTone) => {
        setLoading(true);
        try {
            const template = await emailsService.getDraftTemplate(relanceId, tone);
            setSubject(template.subject);
            setRecipientEmail(template.recipientEmail);
            setSelectedTone(template.selectedTone);
            if (template.bodyTone) {
                setBody(template.bodyTone);
            }
            setAttachments(template.attachments ?? []);
        } finally {
            setLoading(false);
        }
    }, [relanceId]);

    useEffect(() => {
        bccEmailService.get()
            .then(p => {
                setBccEmail(p.bccEmail ?? '');
                // A preset BCC must stay visible, or the reminder silently goes to an address nobody sees.
                if (p.bccEmail) setCopiesOpen(true);
            })
            .catch(e => console.error('Failed to load the BCC preference:', e));
    }, []);

    // Set synchronously by the falling-edge reload below: the body effect runs in the same commit, before
    // `initialLoading` has re-rendered, and would otherwise race the reload that overwrites its result.
    const draftReloadPendingRef = useRef(false);
    const loadInitialDraft = useCallback(async () => {
        setInitialLoading(true);
        setDraftError(null);
        try {
            await loadInitialCustomAware({
                showSaved: (draft, shown) => {
                    setSubject(shown.subject);
                    setRecipientEmail(draft.recipientEmail);
                    setSelectedTone(draft.selectedTone);
                    setBody(shown.bodyTone);
                    setAttachments(draft.attachments ?? []);
                    // Not a latch: a reload that now finds a body must clear a `true` left by an empty read.
                    needsInitialBodyRegenRef.current = !draft.bodyTone;
                    aiDraftRef.current = draft;
                },
                savedReplaced: (draft, aiBody) => {
                    if (aiBody) cachedBodiesRef.current.set(bodyCacheKey(draft.selectedTone, false), aiBody);
                    needsInitialBodyRegenRef.current = false;
                },
                showCustom: draft => {
                    setRecipientEmail(draft.recipientEmail);
                    setSelectedTone(draft.selectedTone);
                },
                showDefault: () => loadDraftTemplate(initialToneRef.current),
            });
        } catch (e) {
            setDraftError(e);
        } finally {
            draftReloadPendingRef.current = false;
            setInitialLoading(false);
        }
    }, [loadDraftTemplate, loadInitialCustomAware]);

    useEffect(() => {
        void loadInitialDraft();
    }, [loadInitialDraft]);

    // The analysis stage creates the email row before the draft stage fills its body, so a draft loaded
    // mid-flight may be empty. Reload on the falling edge only, never on mount.
    const wasRunInFlightRef = useRef(runInFlight);
    useEffect(() => {
        if (wasRunInFlightRef.current && !runInFlight) {
            draftReloadPendingRef.current = true;
            void loadInitialDraft();
        }
        wasRunInFlightRef.current = runInFlight;
    }, [runInFlight, loadInitialDraft]);

    useEffect(() => {
        void relancesService.getAutoAttachments(relanceId).then(setAutomaticAttachments);
    }, [relanceId]);

    const abortActiveStream = () => {
        streamAbortRef.current?.abort();
        streamAbortRef.current = null;
    };

    const refreshBody = useCallback((tone: EmailTone, noAiValue: boolean) => {
        abortActiveStream();
        const controller = new AbortController();
        streamAbortRef.current = controller;

        const runStream = async () => {
            setIsStreaming(true);
            setBody('');
            let accumulated = '';
            try {
                const onAiFallback = () => showToast('error', t(TranslationKey.CustomerFollowUp.Email.Toast.AiFallback));
                for await (const chunk of emailsService.streamBodyRefresh(relanceId, tone, noAiValue, controller.signal, onAiFallback)) {
                    accumulated += chunk;
                    setBody(accumulated);
                }
                cachedBodiesRef.current.set(bodyCacheKey(tone, noAiValue), accumulated);
            } catch (e) {
                if ((e as Error).name !== 'AbortError') {
                    console.error('Email body stream error:', e);
                }
            } finally {
                // An aborted stream's cleanup must not touch state a newer stream already owns
                if (streamAbortRef.current === controller) {
                    setIsStreaming(false);
                    streamAbortRef.current = null;
                }
            }
        };

        void runStream();
    }, [relanceId, showToast, t]);

    const handleToneChange = (choice: ToneChoice) => {
        if (choice === CUSTOM_TONE) {
            selectCustom();
            return;
        }
        const newTone = choice;
        if (newTone === selectedTone && !customDraft.language) return;

        customDraft.clear();
        setSelectedTone(newTone);

        const cached = cachedBodiesRef.current.get(bodyCacheKey(newTone, noAi));
        if (cached !== undefined) {
            abortActiveStream();
            setBody(cached);
            return;
        }

        refreshBody(newTone, noAi);
    };

    useNoAiBodySync({
        ready: !initialLoading && !draftError && !inFlight,
        reloadPendingRef: draftReloadPendingRef,
        noAi,
        tone: selectedTone,
        customTemplateActive: !!customDraft.language,
        needsInitialRegenRef: needsInitialBodyRegenRef,
        refreshBody,
        showCachedAiBody: tone => {
            const cached = cachedBodiesRef.current.get(bodyCacheKey(tone, false));
            if (cached === undefined) return false;
            abortActiveStream();
            setBody(cached);
            return true;
        },
    });

    const handleDeleteAttachment = (filename: string) => {
        const entry = blobUrlsRef.current.get(filename);
        if (entry?.isBlob) URL.revokeObjectURL(entry.url);
        blobUrlsRef.current.delete(filename);
        manualFilesRef.current.delete(filename);
        setAttachments(prev => prev.filter(a => a.filePathName !== filename));
    };

    const handleAddAttachment = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Reset the input first so the user can re-pick the same file after an error
        e.target.value = '';

        const dotIndex = file.name.lastIndexOf('.');
        const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : '';
        if (!ALLOWED_MANUAL_ATTACHMENT_EXTENSIONS.includes(extension)) {
            showToast('error', t(TranslationKey.CustomerFollowUp.Email.Errors.InvalidFormat));
            return;
        }
        if (file.size > MAX_MANUAL_ATTACHMENT_BYTES) {
            showToast('error', t(TranslationKey.CustomerFollowUp.Email.Errors.FileTooLarge));
            return;
        }
        if (manualFilesRef.current.has(file.name)) {
            showToast('error', t(TranslationKey.CustomerFollowUp.Email.Errors.DuplicateFile));
            return;
        }

        const url = URL.createObjectURL(file);
        blobUrlsRef.current.set(file.name, { url, fileType: file.type || null, isBlob: true });
        manualFilesRef.current.set(file.name, file);
        setAttachments(prev => [...prev, {
            filePathName: file.name,
            fileType: file.type || '',
            uploaded: false,
        }]);
    };

    const closePreview = () => {
        previewRequestRef.current = null;
        setPreviewFile(null);
        setPreviewLoading(false);
    };

    const handlePreview = async (filename: string) => {
        const cached = blobUrlsRef.current.get(filename);
        if (cached) {
            previewRequestRef.current = filename;
            setPreviewLoading(false);
            setPreviewFile({ url: cached.url, fileName: filename, fileType: cached.fileType });
            return;
        }

        const autoAttachment = automaticAttachments.find(a => a.fileName === filename);
        if (!autoAttachment) return;

        // Open the modal immediately with a spinner while the blob is fetched.
        previewRequestRef.current = filename;
        setPreviewFile({ url: '', fileName: filename, fileType: null });
        setPreviewLoading(true);
        try {
            const { blob, contentType } = await relancesService.getAutoAttachmentPreview(relanceId, autoAttachment.id);
            const url = URL.createObjectURL(blob);
            blobUrlsRef.current.set(filename, { url, fileType: contentType, isBlob: true });
            // The user may have closed the modal (or opened another file) while fetching.
            if (previewRequestRef.current !== filename) return;
            setPreviewFile({ url, fileName: filename, fileType: contentType });
            setPreviewLoading(false);
        } catch {
            if (previewRequestRef.current !== filename) return;
            closePreview();
            showToast('error', t(TranslationKey.CustomerFollowUp.Toast.Failure));
        }
    };

    const isSending = loading || isStreaming || customDraft.loading;
    // Missing mandatory client info locks the whole form, exactly like the consent checkbox does once ticked.
    const inputsDisabled = confirmed || isSending || hasMissingMandatoryInformation || isSyncing || inFlight;
    const sendBlocked = isSending || sent || !isValid || hasMissingMandatoryInformation || isSyncing || inFlight;

    if (initialLoading || draftError) {
        return (
            <div data-testid="email-tab-error" className="p-xl">
                <Dialogue
                    loading={initialLoading}
                    error={draftError ? localizeApiError(draftError, t) : null}
                    skeleton={<EmailFormSkeleton />}
                />
            </div>
        );
    }

    // Stays in view at the bottom of the window however long the email is.
    const sendBar = (
        <div data-testid="email-send-bar" className="sticky bottom-0 z-10 flex items-center justify-end gap-m h-16.5 bg-surface/70 backdrop-blur-md">
            <Button variant="secondary"
                data-testid="email-send-btn"
                type="button"
                disabled={!confirmed || sendBlocked}
                loading={loading && sendingAction === 'send'}
                onClick={() => { setSendingAction('send'); void handleSend(onSend); }}
            >
                {t(TranslationKey.CustomerFollowUp.Email.Send)}
            </Button>
            <Button
                data-testid="email-send-and-next-btn"
                type="button"
                disabled={!confirmed || sendBlocked}
                loading={loading && sendingAction === 'sendAndNext'}
                onClick={() => { setSendingAction('sendAndNext'); void handleSend(onSendAndNext); }}
            >
                {t(TranslationKey.CustomerFollowUp.Email.SendAndNext)}
            </Button>
        </div>
    );

    return (
        <div className="flex flex-col gap-xl w-full">
        <div className="flex flex-wrap items-center justify-between gap-m">
            <ToneSelector value={customDraft.language ? CUSTOM_TONE : selectedTone} onChange={handleToneChange}
                disabled={inputsDisabled} showCustom={customDraft.hasTemplate || !!customDraft.language} />
            {isCustomEmailTemplateEnabled && <CustomTemplateToolbar
                activeLanguage={customDraft.language}
                languages={customDraft.languages}
                hasTemplate={customDraft.hasTemplate}
                disabled={inputsDisabled}
                currentSubject={subject}
                currentBody={body}
                onSelectLanguage={selectCustom}
                onCreated={() => selectCustom()}
            />}
        </div>
        <div className="rounded-lg border border-line-subtle bg-primary-50 shadow-weak divide-y divide-line-subtle">
            <div className={`${FIELD_ROW_CHROME} grid-cols-[3.5rem_1fr_auto]`}>
                <label htmlFor="email-address" className="text-s font-semibold text-primary-800 whitespace-nowrap">
                    {t(TranslationKey.CustomerFollowUp.Email.Recipient)}
                </label>
                <div>
                    <FormInput
                        variant="inline"
                        id="email-address"
                        data-testid="email-recipient"
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        onBlur={touch('recipientEmail')}
                        disabled={inputsDisabled}
                        aria-invalid={!!getError('recipientEmail')}
                        aria-describedby={getError('recipientEmail') ? 'email-address-err' : undefined}
                    />
                    <ValidationMessage id="email-address-err" data-testid="email-recipient-error" message={getError('recipientEmail')} />
                </div>
                <button
                    type="button"
                    data-testid="email-copies-toggle"
                    aria-expanded={copiesShown}
                    aria-controls="email-copies"
                    onClick={() => setCopiesOpen(open => !open)}
                    className="ml-s text-s font-semibold text-content whitespace-nowrap rounded-sm hover:text-accent-400 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring cursor-pointer"
                >
                    {t(TranslationKey.CustomerFollowUp.Email.Copies)}
                </button>
            </div>

            <div id="email-copies" data-testid="email-copies" hidden={!copiesShown}>
                {([['cc', ccEmail, setCcEmail, TranslationKey.CustomerFollowUp.Email.Cc], ['bcc', bccEmail, setBccEmail, TranslationKey.CustomerFollowUp.Email.Bcc]] as const).map(([kind, value, setValue, labelKey]) => {
                    const field: EmailField = `${kind}Email`;
                    return (
                        <div key={kind} className={`${FIELD_ROW_CLASS} border-t border-line-subtle first:border-t-0`}>
                            <label htmlFor={`email-${kind}`} className="text-s font-semibold text-primary-800 whitespace-nowrap">
                                {t(labelKey)}
                            </label>
                            <div>
                                <FormInput
                                    variant="inline"
                                    id={`email-${kind}`}
                                    data-testid={`email-${kind}`}
                                    type="email"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    onBlur={touch(field)}
                                    disabled={inputsDisabled}
                                    aria-invalid={!!getError(field)}
                                    aria-describedby={getError(field) ? `email-${kind}-err` : undefined}
                                />
                                <ValidationMessage id={`email-${kind}-err`} data-testid={`email-${kind}-error`} message={getError(field)} />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={FIELD_ROW_CLASS}>
                <label htmlFor="subject" className="text-s font-semibold text-primary-800 whitespace-nowrap">
                    {t(TranslationKey.CustomerFollowUp.Email.Subject)}
                </label>
                <div>
                    <FormInput
                        variant="inline"
                        id="subject"
                        data-testid="email-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        onBlur={touch('subject')}
                        disabled={inputsDisabled}
                        aria-invalid={!!getError('subject')}
                        aria-describedby={getError('subject') ? 'subject-err' : undefined}
                    />
                    <ValidationMessage id="subject-err" data-testid="email-subject-error" message={getError('subject')} />
                </div>
            </div>

            <div className="flex flex-col">
                {/* No right padding here: the textarea carries it, so its scrollbar sits against the box's edge. */}
                <div className="field-row-focus pl-xl py-l">
                    {inFlight || (isStreaming && !body) ? (
                        <div className="pr-xl"><EmailBodySkeleton /></div>
                    ) : (
                        <TextArea
                            variant="inline"
                            id="body"
                            data-testid="email-body"
                            className="pr-xl"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            onBlur={touch('body')}
                            rows={10}
                            disabled={inputsDisabled}
                            aria-invalid={!!getError('body')}
                            aria-describedby={getError('body') ? 'body-err' : undefined}
                        />
                    )}
                </div>
                <div className="flex flex-col gap-m px-xl pb-l">
                    <ValidationMessage id="body-err" data-testid="email-body-error" message={getError('body')} />
                    <hr className="w-10 border-line-subtle" />
                    <p data-testid="email-signature-hint" className="text-s text-content-tertiary">
                        {t(TranslationKey.CustomerFollowUp.Email.SignatureHint)}
                    </p>
                </div>
            </div>

            <div data-testid="email-attachments-footer" className="px-xl py-m flex flex-col gap-s">
                {sortedAutomaticAttachments.length > 0 && (
                    <div data-testid="email-auto-attachments-section">
                        <AttachmentList
                            files={sortedAutomaticAttachments.map(a => a.fileName)}
                            onDelete={(filename) => setPendingDeleteAutoAttachmentName(filename)}
                            onPreview={handlePreview}
                            previewableFiles={previewableAutoAttachments}
                            disabled={inputsDisabled}
                            rowTestIdPrefix="email-auto-attachment-row"
                            deleteBtnTestIdPrefix="email-auto-attachment-delete-btn"
                        />
                    </div>
                )}
                <AttachmentList
                    files={attachments.map(a => a.filePathName)}
                    onDelete={handleDeleteAttachment}
                    onAdd={handleAddAttachment}
                    onPreview={handlePreview}
                    disabled={inputsDisabled}
                    addBtnTestId="email-attachment-add-btn"
                    rowTestIdPrefix="email-attachment-row"
                    deleteBtnTestIdPrefix="email-attachment-delete-btn"
                    previewBtnTestId="email-attachment-preview-btn"
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={MANUAL_ATTACHMENT_ACCEPT}
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={inputsDisabled}
                />
            </div>
        </div>

            {hasPaymentLinks && (
                <Checkbox
                    data-testid="email-payment-link-checkbox"
                    label={t(TranslationKey.CustomerFollowUp.Email.PaymentLink.Label, { count: paymentLinkCount ?? undefined })}
                    checked={includePaymentLinks}
                    onChange={(e) => setIncludePaymentLinks(e.target.checked)}
                    disabled={inputsDisabled}
                />
            )}

            {/* Styled like the address block at the top of the page, to give the approval its weight. */}
            <div className="px-m py-l rounded-lg bg-surface-secondary text-content">
                <Checkbox
                    data-testid="email-confirm-checkbox"
                    size="xs"
                    label={t(TranslationKey.CustomerFollowUp.Email.Confirmation)}
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    disabled={sendBlocked}
                />
            </div>

            {sendBarContainer ? createPortal(sendBar, sendBarContainer) : sendBar}
            <FileViewerModal file={previewFile} loading={previewLoading} onClose={closePreview} />

            {pendingDeleteAutoAttachmentName && (
                <ConfirmModal
                    heading={t(TranslationKey.CustomerFollowUp.Email.DeleteAutoAttachmentDialog.Heading)}
                    body={t(TranslationKey.CustomerFollowUp.Email.DeleteAutoAttachmentDialog.Body)}
                    confirmLabel={t(TranslationKey.CustomerFollowUp.Email.DeleteAutoAttachmentDialog.Confirm)}
                    cancelLabel={t(TranslationKey.CustomerFollowUp.Email.DeleteAutoAttachmentDialog.Cancel)}
                    onConfirm={handleDeleteAutoAttachmentConfirm}
                    onCancel={() => setPendingDeleteAutoAttachmentName(null)}
                    modalTestId="auto-attachment-delete-confirm-modal"
                    confirmBtnTestId="confirm-modal-confirm-btn"
                />
            )}

            {showNoAttachmentsWarning && (
                <ConfirmModal
                    testId="no-attachments-warning-modal"
                    heading={t(TranslationKey.CustomerFollowUp.Email.NoAttachmentsDialog.Heading)}
                    body={t(TranslationKey.CustomerFollowUp.Email.NoAttachmentsDialog.Body)}
                    confirmLabel={t(TranslationKey.CustomerFollowUp.Email.NoAttachmentsDialog.Confirm)}
                    cancelLabel={t(TranslationKey.CustomerFollowUp.Email.NoAttachmentsDialog.Cancel)}
                    onConfirm={handleNoAttachmentsConfirm}
                    onCancel={() => { setShowNoAttachmentsWarning(false); setSendingAction(null); }}
                />
            )}

            {attachmentFetchFailedFiles && (
                <ConfirmModal
                    testId="attachment-fetch-failed-modal"
                    heading={t(TranslationKey.CustomerFollowUp.Email.AttachmentFetchFailedDialog.Heading)}
                    body={t(TranslationKey.CustomerFollowUp.Email.AttachmentFetchFailedDialog.Body)}
                    confirmLabel={t(TranslationKey.CustomerFollowUp.Email.AttachmentFetchFailedDialog.Confirm)}
                    cancelLabel={t(TranslationKey.CustomerFollowUp.Email.AttachmentFetchFailedDialog.Cancel)}
                    onConfirm={handleAttachmentFetchFailedConfirm}
                    onCancel={() => { setAttachmentFetchFailedFiles(null); setSendingAction(null); }}
                />
            )}
        </div>
    );
};
